import { lazy, ComponentType } from 'react';

/**
 * A wrapper around React.lazy that handles chunk load errors (e.g. when a new version
 * is deployed and the old chunk hashes are no longer available).
 * 
 * If a chunk fails to load, it will force a window reload to fetch the new scripts.
 * It uses sessionStorage to ensure we don't end up in an infinite reload loop if
 * the target js file stays permanently missing.
 */
export const lazyWithRetry = <T extends ComponentType<any>>(
    componentImport: () => Promise<{ default: T }>
) => {
    return lazy(async () => {
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
            window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
        );

        try {
            const component = await componentImport();
            // On success, reset the session storage flag so future navigations can retry if needed
            window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
            return component;
        } catch (error: any) {
            if (!pageHasAlreadyBeenForceRefreshed) {
                // Check if this looks like a chunk loading/MIME type error
                const isChunkLoadError = error?.name === 'ChunkLoadError';
                const isMimeTypeError = error?.message?.includes("MIME type");
                const isFailedToFetch = error?.message?.includes("Failed to fetch dynamically imported module") || error?.message?.includes("Importing a module script failed");

                if (isChunkLoadError || isMimeTypeError || isFailedToFetch) {
                    window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
                    window.location.reload();

                    // Return a hanging promise to prevent React from trying to render an error state
                    // while the reload happens
                    return new Promise<{ default: T }>(() => { });
                }
            }

            // If we already refreshed or it's a different error, throw it so Error Boundaries can catch it
            throw error;
        }
    });
};
